import {css, cx as addStyles} from '@emotion/css';
import React, {HTMLAttributes} from 'react';
import {Button} from './Button';
import {formatValueToDollar, theme} from './Common';
import {Modal} from './Modal';

export type CustomerPortalModalCheckNextProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => unknown;
  value: number;
  name: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  cashOut: () => unknown;
  disabled: boolean;
};

export const CustomerPortalModalCheckNext = ({
  isOpen,
  setIsOpen,
  value,
  name,
  address,
  address2,
  city,
  state,
  zipcode,
  cashOut,
  disabled,
}: CustomerPortalModalCheckNextProps) => (
  <Modal title='Check Details' isOpen={isOpen} setModal={setIsOpen}>
    <Content>
      <Title>Confirm Information</Title>
      <Value>{formatValueToDollar(value, {maximumFractionDigits: 2})}</Value>
      <InfoContainer>
        <InfoWrapper>
          <InfoText>NAME (RECIPIENT)</InfoText>
          <InfoValue>{name}</InfoValue>
        </InfoWrapper>

        <InfoWrapper>
          <InfoText>ADDRESS</InfoText>
          <InfoValue>{address}</InfoValue>
        </InfoWrapper>

        <InfoWrapper>
          <InfoText>ADDRESS LINE 2</InfoText>
          <InfoValue>{address2}</InfoValue>
        </InfoWrapper>

        <DownsideContainer>
          <InfoWrapper className='city'>
            <InfoText>CITY</InfoText>
            <InfoValue>{city}</InfoValue>
          </InfoWrapper>
          <InfoWrapper>
            <InfoText>STATE</InfoText>
            <InfoValue>{state}</InfoValue>
          </InfoWrapper>
          <InfoWrapper className='zipcode'>
            <InfoText>ZIPCODE</InfoText>
            <InfoValue>{zipcode}</InfoValue>
          </InfoWrapper>
        </DownsideContainer>
      </InfoContainer>

      <ButtonContainer>
        <Button buttonStyle='primary' onClick={cashOut} disabled={disabled}>
          Withdraw
        </Button>
      </ButtonContainer>
    </Content>
  </Modal>
);
const flexStartColumn = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
});
const Content = (args: HTMLAttributes<HTMLDivElement>) => (
  <div className={flexStartColumn} {...args} />
);
const textBase = css({fontFamily: theme.font.family, textAlign: 'left'});
const Title = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={addStyles(
      textBase,
      css({
        color: theme.colors.darkGray,
        fontWeight: theme.font.medium,
        fontSize: theme.font.sizes.medium,
      }),
    )}
    {...args}
  />
);
const Value = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={addStyles(
      textBase,
      css({
        color: theme.colors.darkBlue,
        fontWeight: theme.font.medium,
        fontSize: theme.font.sizes.large,
        marginTop: theme.spacings.xxsmall,
      }),
    )}
    {...args}
  />
);
const InfoContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      flexStartColumn,
      css({
        marginTop: theme.spacings.large,
        [theme.mediaMaxWidth.small]: {marginTop: theme.spacings.xsmall},
      }),
    )}
    {...args}
  />
);
const InfoWrapper = (args: HTMLAttributes<HTMLDivElement>) => (
  <div className={addStyles(flexStartColumn, css({marginTop: theme.spacings.small}))} {...args} />
);
const InfoText = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={addStyles(
      textBase,
      css({
        color: theme.colors.darkGray,
        fontWeight: theme.font.medium,
        fontSize: theme.font.sizes.small,
      }),
    )}
    {...args}
  />
);
const InfoValue = (args: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={addStyles(
      textBase,
      css({
        color: theme.colors.darkGray,
        fontWeight: theme.font.normal,
        fontSize: theme.font.sizes.small,
        marginTop: theme.spacings.xxsmall,
      }),
    )}
    {...args}
  />
);
const centeredAlignedRow = css({display: 'flex', flexDirection: 'row', alignItems: 'center'});
const DownsideContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      centeredAlignedRow,
      css({
        gap: `calc(${theme.spacings.large}*2)`,
        flexWrap: 'wrap',
        [theme.mediaMaxWidth.small]: {
          gap: 0,
          marginTop: '-8px',
          '.city': {width: '350px'},
          '.zipcode': {marginLeft: '80px'},
        },
      }),
    )}
    {...args}
  />
);
const ButtonContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      centeredAlignedRow,
      css({
        justifyContent: 'center',
        width: '100%',
        marginTop: theme.spacings.large,
        [theme.mediaMaxWidth.small]: {marginTop: theme.spacings.medium},
      }),
    )}
    {...args}
  />
);
